import {
	VisualEditFrameEntry,
	VisualEdit,
	TransformVisualEdit,
	TextVisualEdit,
	ImageVisualEdit,
	StyleVisualEdit,
	Frame,
} from './interfaces';

// Creates a unique hash for a set of visual edits for cache key and URL uniqueness
// Returns null if there are no visual edits
export function createHash(visualEdits: VisualEdit[]): string | null {
	if (visualEdits.length === 0) {
		return null;
	}

	// Sort edits deterministically: first by kind, then by elementId
	const sorted = [...visualEdits].sort((a, b) => {
		// First sort by kind (for future extensibility when multiple edit types exist)
		const aKind = (a as { kind: string }).kind ?? '';
		const bKind = (b as { kind: string }).kind ?? '';
		if (aKind !== bKind) {
			return aKind.localeCompare(bKind);
		}
		// Then sort by elementId
		const aId = typeof a.elementId === 'string' ? parseInt(a.elementId, 10) : a.elementId;
		const bId = typeof b.elementId === 'string' ? parseInt(b.elementId, 10) : b.elementId;
		// Handle NaN cases - fallback to string comparison
		if (Number.isNaN(aId) || Number.isNaN(bId)) {
			const aStr = String(a.elementId ?? '');
			const bStr = String(b.elementId ?? '');
			return aStr.localeCompare(bStr);
		}
		return aId - bId;
	});

	const hashInput = sorted
		.map((edit: VisualEdit) => {
			if (edit.kind === 'transform') {
				const transformEdit = edit as TransformVisualEdit;
				// Include kind in hash for robustness and future extensibility
				return `${transformEdit.kind}:${transformEdit.elementId}:${transformEdit.transform.x},${transformEdit.transform.y}`;
			} else if (edit.kind === 'text') {
				const textEdit = edit as TextVisualEdit;
				return `${textEdit.kind}:${textEdit.elementId}:${textEdit.text ?? ''}:${textEdit.innerHTML ?? ''}`;
			} else if (edit.kind === 'image') {
				const imageEdit = edit as ImageVisualEdit;
				return `${imageEdit.kind}:${imageEdit.elementId}:${imageEdit.imageUrl}:${imageEdit.altTag}`;
			} else if (edit.kind === 'style') {
				const styleEdit = edit as StyleVisualEdit;
				// CSS values can contain ',' and '=' (rgba(...), font-family: "a, b"),
				// so a "k=v,k=v" join would collide. JSON.stringify of a sorted object
				// is unambiguous and stable.
				const sortedStyles = Object.fromEntries(
					Object.entries(styleEdit.styles).sort(([a], [b]) => a.localeCompare(b))
				);
				return `${styleEdit.kind}:${styleEdit.elementId}:${JSON.stringify(sortedStyles)}`;
			}
			// Future visual edit types will be handled here
			console.error('Unknown visual edit type:', edit);
			// Return fallback to prevent undefined in hash string
			return `unknown:${(edit as { elementId?: string }).elementId || ''}`;
		})
		.join('|');

	// Simple hash function that works in both browser and Node.js
	let hash = 0;
	for (let i = 0; i < hashInput.length; i++) {
		const char = hashInput.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash = hash & hash; // Convert to 32bit integer
	}
	return Math.abs(hash).toString(16).substring(0, 16);
}

// Get the hash for a frame's currently active visual edits
// Returns null if frame doesn't currently have visual edits active or the edits can't be found
export function getActiveVisualEditsHash(frame: Frame): string | null {
	if (!frame.metadata?.visualEditActive || !frame.siteId) {
		return null;
	}

	const visualEditEntry = frame.entries?.find(
		(e): e is VisualEditFrameEntry =>
			e.kind === 'visualEdit' && 'siteId' in e && e.siteId === frame.siteId
	);

	if (!visualEditEntry) return null;

	return createHash(visualEditEntry.visualEdits);
}
